export type CityPreview = {
  id: string;
  name: string;
  state: string;
  stats: {
    fields: number;
    categories: number;
    updated: string;
    sources: number;
  };
  categoryChips: { label: string; count: number }[];
  unlockedRows: { field: string; value: string; citation: string; details?: string; keys?: string[] }[];
  lockedRows: { field: string }[];
  moreCount: number;
  gotcha: { title: string; body: string };
  sourceLine: string;
};

export const SOLAR_CITIES: CityPreview[] = [
  {
    id: "sacramento-ca",
    name: "Sacramento",
    state: "CA",
    stats: { fields: 247, categories: 18, updated: "3 days ago", sources: 110 },
    categoryChips: [
      { label: "Zoning", count: 32 },
      { label: "Fire", count: 19 },
      { label: "Electrical", count: 24 },
      { label: "Structural", count: 21 },
      { label: "Permitting", count: 41 },
      { label: "Utility / IX", count: 28 },
    ],
    unlockedRows: [
      { field: "Electrical Code Edition", value: "2023 National Electrical Code", citation: "California adopted codes" },
      { field: "Roof Fire Classification Rating", value: "Class A", citation: "CA Building Code" },
      { field: "Permit Cost (Residential)", value: "$450 ≤15 kW; +$15/kW above 15 kW", citation: "Sacramento Fee Schedule 2025", keys: ["a-pm-fee"] },
      {
        field: "Professional Engineering Required (Residential)",
        value: "Structural PE stamp required for specific roof conditions",
        citation: "Sacramento Building Division",
        keys: ["d-pe-required","a-pm-structural-stamp","a-pm-electrical-stamp"],
        details: [
          "Required for tile roof installations",
          "Required for roofs with multiple existing overlays",
          "Standard comp-shingle single-layer roofs typically exempt",
        ].join("\n"),
      },
      {
        field: "Rooftop Fire Access Pathways (Residential)",
        value: "18–36 in setbacks; 36 in pathway widths",
        citation: "CA Fire Code & local amendments",
        keys: ["d-fire-setback","s-fire-setback","s-ridgeline-setback"],
        details: [
          "Distance to ridge (arrays ≤33% roof area): 18 in",
          "Distance to ridge (arrays >33% roof area): 36 in",
          "Eave to ridge path width: 36 in",
          "Distance to skylights: 36 in",
          "Non-habitable structures: exempt",
        ].join("\n"),
      },
    ],
    lockedRows: [
      { field: "Rapid shutdown labeling specifics" },
      { field: "Reroof permit trigger threshold" },
      { field: "Battery storage setback from openings" },
      { field: "MPU panel upgrade requirement" },
      { field: "HOA pre-approval carve-outs" },
      { field: "Conduit run color / labeling rules" },
      { field: "Structural attachment spacing (tile)" },
      { field: "SMUD interconnection requirements" },
    ],
    moreCount: 234,
    gotcha: {
      title: "Most teams miss this",
      body: "Sacramento's PE stamp requirement quietly kicks in the moment you touch a tile roof or a roof with more than one existing overlay — even on otherwise standard residential jobs. Skipping it is the #1 cause of plan-review redlines here.",
    },
    sourceLine: "Sourced from Sacramento Building Division, CA Building & Fire Code adopted amendments, and SMUD interconnection manual — verified May 2026.",
  },
  {
    id: "rockville-md",
    name: "Rockville",
    state: "MD",
    stats: { fields: 231, categories: 17, updated: "1 week ago", sources: 195 },
    categoryChips: [
      { label: "Zoning", count: 28 },
      { label: "Fire", count: 22 },
      { label: "Electrical", count: 26 },
      { label: "Structural", count: 18 },
      { label: "Permitting", count: 38 },
      { label: "Utility / IX", count: 31 },
    ],
    unlockedRows: [
      { field: "Electrical Code Edition", value: "2023 National Electrical Code", citation: "Maryland adopted codes" },
      { field: "Roof Fire Classification Rating", value: "Class A", citation: "Rockville Building Code" },
      { field: "Permit Cost (Residential)", value: "$120 flat", citation: "Rockville Fee Schedule 2025", keys: ["a-pm-fee"] },
      {
        field: "Professional Engineering Required (Residential)",
        value: "Structural & Electrical PE stamps conditionally required",
        citation: "Rockville Permitting Division",
        keys: ["d-pe-required","a-pm-structural-stamp","a-pm-electrical-stamp"],
        details: [
          "Structural PE: required if installation involves structural modifications",
          "Electrical PE: required for specific electrical components / configurations",
          "Mechanical PE: generally not required for typical residential systems",
        ].join("\n"),
      },
      {
        field: "Rooftop Fire Access Pathways (Residential)",
        value: "Two 36 in pathways on separate roof planes; ridge 18–36 in by coverage",
        citation: "IFC w/ Maryland amendments",
        keys: ["d-fire-setback","s-fire-setback","s-ridgeline-setback"],
        details: [
          "Two 36-inch wide pathways required on separate roof planes",
          "Ridge pathway: 18 in for PV coverage ≤ threshold",
          "Ridge pathway: 36 in for higher PV coverage",
          "No separately codified values for eave, valley, or hip pathways",
        ].join("\n"),
      },
    ],
    lockedRows: [
      { field: "Pepco interconnection queue rules" },
      { field: "Battery storage indoor placement limits" },
      { field: "Tree ordinance impact on array siting" },
      { field: "Historic district overlay restrictions" },
      { field: "Service entrance derate triggers" },
      { field: "Required arc-fault protection scope" },
      { field: "Roof penetration sealing standard" },
      { field: "Net metering documentation set" },
    ],
    moreCount: 218,
    gotcha: {
      title: "Most teams miss this",
      body: "Rockville's PE-stamp trigger is condition-based, not size-based — even a small system can require Structural and Electrical PE stamps if it touches the right component. Teams pricing off kW alone routinely under-bid.",
    },
    sourceLine: "Sourced from City of Rockville Permitting Division, Maryland adopted IFC/IRC amendments, and Pepco interconnection manual — verified May 2026.",
  },
  {
    id: "dallas-tx",
    name: "Dallas",
    state: "TX",
    stats: { fields: 263, categories: 19, updated: "5 days ago", sources: 249 },
    categoryChips: [
      { label: "Zoning", count: 30 },
      { label: "Fire", count: 24 },
      { label: "Electrical", count: 27 },
      { label: "Structural", count: 26 },
      { label: "Permitting", count: 44 },
      { label: "Utility / IX", count: 29 },
    ],
    unlockedRows: [
      { field: "Electrical Code Edition", value: "2020 National Electrical Code", citation: "Dallas adopted codes" },
      { field: "Roof Fire Classification Rating", value: "Class B", citation: "Dallas Building Code" },
      { field: "Permit Cost (Residential)", value: "$577", citation: "Dallas Fee Schedule 2025", keys: ["a-pm-fee"] },
      {
        field: "Professional Engineering Required (Residential)",
        value: "Structural PE always; Electrical PE at ≥100 kW inverter",
        citation: "Dallas Building Inspection",
        keys: ["d-pe-required","a-pm-structural-stamp","a-pm-electrical-stamp"],
        details: [
          "Structural PE stamp required for all residential PV and BIPV systems (attachment & load review)",
          "Electrical PE stamp required for systems with ≥100 kW inverter capacity",
          "Applies to both PV and BIPV residential installations",
        ].join("\n"),
      },
      {
        field: "Rooftop Fire Access Pathways (Residential)",
        value: "Ridge 18 in (≤33% coverage) / 36 in (>33%); 36 in pathway widths",
        citation: "Dallas Fire Code amendments",
        keys: ["d-fire-setback","s-fire-setback","s-ridgeline-setback"],
        details: [
          "Ridge setback: 18 in for ≤33% roof coverage",
          "Ridge setback: 36 in for >33% roof coverage",
          "Pathways must be at least 36 in wide",
          "Exceptions available if sprinklers are installed",
        ].join("\n"),
      },
    ],
    lockedRows: [
      { field: "Oncor interconnection size-tier triggers" },
      { field: "Sprinkler-based fire setback exceptions" },
      { field: "Wind exposure category by district" },
      { field: "Battery storage egress clearances" },
      { field: "Required energy code compliance items" },
      { field: "Re-roof concurrent permit logic" },
      { field: "MPU triggers under 200A service" },
      { field: "Net metering aggregation eligibility" },
    ],
    moreCount: 250,
    gotcha: {
      title: "Most teams miss this",
      body: "Dallas still runs on the 2020 NEC — not the 2023 most teams are designing to. Submitting 2023-cycle plans without reconciling rapid-shutdown and labeling deltas fails plan review on the first cycle.",
    },
    sourceLine: "Sourced from Dallas Building Inspection, Dallas Fire Code amendments, and Oncor interconnection manual — verified May 2026.",
  },
];

export const HOMEBUILDER_CITIES: CityPreview[] = [
  {
    id: "phoenix-az",
    name: "Phoenix",
    state: "AZ",
    stats: { fields: 274, categories: 19, updated: "4 days ago", sources: 484 },
    categoryChips: [
      { label: "Zoning", count: 41 },
      { label: "Site Plan", count: 28 },
      { label: "Building", count: 36 },
      { label: "Stormwater", count: 24 },
      { label: "Permitting", count: 38 },
      { label: "Impact Fees", count: 19 },
    ],
    unlockedRows: [
      { field: "Residential Code Edition", value: "2024 International Residential Code", citation: "City of Phoenix adopted codes", keys: ["hb-residential-code"] },
      { field: "Building Permit Expiration", value: "24 months", citation: "Phoenix City Code" },
      { field: "Noise Allowances (Residential)", value: "55 dBA (residential limit)", citation: "Phoenix City Code" },
      {
        field: "Front Yard Setback",
        value: "20–40 ft — varies across 13 residential zones",
        citation: "Phoenix Zoning Ordinance",
        keys: ["hb-front-setback"],
        details: [
          "RE-43 Single-Family Residential Estate: 40 ft",
          "RE-35 Single-Family Residential Estate: 40 ft; 25 ft (PRD)",
          "RE-24 Single-Family Residential Estate: 30 ft",
          "R1-18 Single-Family Residential: 25 ft",
          "R1-14 Single-Family Residential: 30 ft",
          "R1-10 Single-Family Residential: 25 ft; 10 ft (PRD)",
          "R1-8 Single-Family Residential: 20 ft; 10 ft (PRD)",
          "R1-6 Single-Family Residential: 20 ft; 10 ft (PRD)",
          "R-2 Multi-Family: 20 ft; 10 ft (PRD)",
          "R-3 Multi-Family: 25 ft; 10 ft (PRD)",
          "R-3A Multi-Family: 25 ft; 10 ft (PRD)",
          "R-4 Multi-Family: 20 ft; 10 ft (PRD)",
          "R-4A Multi-Family: 20 ft",
          "R-5 Multi-Family: 20 ft; 10 ft (PRD)",
        ].join("\n"),
      },
      {
        field: "Min Lot Size",
        value: "Tracked across 9 residential zones (RE-35 → Residential Infill R-I + R-4)",
        citation: "Phoenix Zoning Ordinance",
        keys: ["hb-lot-size"],
        details: [
          "RE-35: 35,000 sq ft",
          "R1-18: 18,000 sq ft",
          "R1-10: Not specified",
          "R1-8: Not specified",
          "R1-6: Not specified",
          "R-3: Not specified",
          "R-4A: 6,000 sq ft",
          "Residential Infill R-I + R-3: 6,000 sq ft (per dwelling unit not less than 2,000 sq ft)",
          "Residential Infill R-I + R-4: 6,000 sq ft (per dwelling unit not less than 1,250 sq ft)",
        ].join("\n"),
      },
    ],
    lockedRows: [
      { field: "PRD setback reductions by zone" },
      { field: "Max building height by district" },
      { field: "Impervious coverage caps" },
      { field: "Tree replacement requirements" },
      { field: "Stormwater detention triggers" },
      { field: "Impact fee schedule by bedroom count" },
      { field: "Expedited permitting eligibility" },
      { field: "Plan set submittal requirements" },
    ],
    moreCount: 261,
    gotcha: {
      title: "Most teams miss this",
      body: "Phoenix front setbacks swing from 20 ft to 40 ft across 13 residential zones — and PRD overlays drop several down to 10 ft. Pulling the wrong zone's number is the #1 cause of plan-review redlines here.",
    },
    sourceLine: "Sourced from City of Phoenix Planning & Development Dept., Phoenix Zoning Ordinance, and adopted 2024 IRC — verified May 2026.",
  },
  {
    id: "south-haven-mi",
    name: "South Haven",
    state: "MI",
    stats: { fields: 198, categories: 16, updated: "1 week ago", sources: 52 },
    categoryChips: [
      { label: "Zoning", count: 34 },
      { label: "Site Plan", count: 22 },
      { label: "Building", count: 31 },
      { label: "Stormwater", count: 18 },
      { label: "Permitting", count: 27 },
      { label: "Impact Fees", count: 14 },
    ],
    unlockedRows: [
      { field: "Residential Code Edition", value: "2015 Michigan Residential Code", citation: "Michigan adopted codes", keys: ["hb-residential-code"] },
      { field: "Building Permit Expiration", value: "6 months", citation: "South Haven Code of Ordinances" },
      { field: "Noise Allowances (Residential)", value: "75 dBA (residential limit)", citation: "South Haven Code of Ordinances" },
      {
        field: "Front Yard Setback",
        value: "3–25 ft — varies across 5 residential zones",
        citation: "South Haven Zoning Ordinance",
        keys: ["hb-front-setback"],
        details: [
          "R-1A Residential District: 15 ft",
          "R-1B Residential District: 25 ft",
          "R-1C Residential District: 3 ft",
          "R-2 Single & Two-Family Residential: 25 ft",
          "RM-1 Multi-Family Residential: 25 ft",
        ].join("\n"),
      },
      {
        field: "Min Lot Size",
        value: "2,178 – 35,000 sq ft — varies by zone & utility service area",
        citation: "South Haven Zoning Ordinance",
        keys: ["hb-lot-size"],
        details: [
          "R-1A Single Family Residential: 0.115 ac (5,000 sq ft)",
          "R-1B Single Family Residential: 0.193 ac (8,400 sq ft)",
          "R-1C Single Family Residential: 0.05 ac (2,178 sq ft)",
          "R-2 Single & Two-Family Residential: 0.193 ac (8,400 sq ft)",
          "RM-1 Multiple-Family Residential: 0.2 ac (8,712 sq ft)",
          "Complete Utility Subdivision: 0.193 ac (8,400 sq ft), 70 ft frontage",
          "Partial Utility Subdivision: 0.23 ac (10,000 sq ft), 80 ft frontage",
          "Potential Utility Subdivision (Primary Service Area): 0.31 ac (13,500 sq ft), 90 ft frontage",
          "Potential Utility Subdivision (Secondary Service Area): 0.41 ac (18,000 sq ft), 110 ft frontage",
          "Private Service Subdivision: 0.8 ac (35,000 sq ft), 165 ft frontage",
        ].join("\n"),
      },
    ],
    lockedRows: [
      { field: "Utility service-area lot minimums" },
      { field: "Lakefront overlay setback rules" },
      { field: "Max building height by district" },
      { field: "Stormwater detention triggers" },
      { field: "Tree preservation requirements" },
      { field: "Impact fee schedule" },
      { field: "Plan set submittal requirements" },
      { field: "Inspection sequence" },
    ],
    moreCount: 185,
    gotcha: {
      title: "Most teams miss this",
      body: "South Haven's lot-size minimums change based on whether the parcel sits in a Complete, Partial, Potential, or Private utility-service subdivision — not just the zoning district. Same R-2 zone, four different minimums.",
    },
    sourceLine: "Sourced from City of South Haven Zoning Ordinance, South Haven Code of Ordinances, and 2015 Michigan Residential Code — verified May 2026.",
  },
];

export const SOLAR_TOTAL_FIELDS = 90;
export const HOMEBUILDER_TOTAL_FIELDS = 120;
